/**
 * @module ClaimsFormularPublic
 * @description Öffentliche Standalone-Seite für Claims via Token-Link.
 *   Kein Login nötig — Token identifiziert den Kunden.
 *   URL: /claims/submit?token=abc123
 */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import ClaimsFormular from './ClaimsFormular';
import { ShieldAlert, AlertTriangle } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

export default function ClaimsFormularPublic() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { t } = useLanguage();
  const { theme } = useTheme();
  const [status, setStatus] = useState('loading'); // loading | valid | invalid
  const [customerName, setCustomerName] = useState('');
  const [customerId, setCustomerId] = useState(null);

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    fetch(`${API}/api/claims-portal/public/verify?token=${encodeURIComponent(token)}`)
      .then(r => r.json())
      .then(d => {
        if (d.valid) {
          setCustomerName(d.customer_name);
          setCustomerId(d.customer_id);
          setStatus('valid');
        } else {
          setStatus('invalid');
        }
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  // Loading
  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-primary)' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #338edc 0%, #2b7bc4 100%)' }}>
            <span className="text-white font-bold text-lg">W</span>
          </div>
          <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // Invalid token
  if (status === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg-primary)' }}>
        <div className="w-full max-w-md text-center" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: 16, padding: 40 }}>
          <div className="flex justify-center mb-6">
            <img src={theme === 'dark' ? '/weship-logo-light.png' : '/weship-logo.png'} alt="WeShip" className="h-8" />
          </div>
          <div className="flex justify-center mb-4">
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertTriangle size={28} style={{ color: '#EF4444' }} />
            </div>
          </div>
          <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
            {t('claims.publicForm.invalidTitle') || 'Invalid Link'}
          </h2>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {t('claims.publicForm.invalidToken')}
          </p>
        </div>
      </div>
    );
  }

  // Valid — show form
  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* Header with logo */}
      <div className="border-b" style={{ borderColor: 'var(--border-card)', background: 'var(--bg-card)' }}>
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <img src={theme === 'dark' ? '/weship-logo-light.png' : '/weship-logo.png'} alt="WeShip" className="h-7" />
          <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
            {customerName}
          </span>
        </div>
      </div>

      {/* Welcome banner */}
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-2">
        <div className="flex items-center gap-3 mb-2">
          <ShieldAlert size={20} style={{ color: '#338edc' }} />
          <h1 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            {(t('claims.publicForm.welcome') || 'Damage claim for {customerName}').replace('{customerName}', customerName)}
          </h1>
        </div>
      </div>

      {/* Render the actual form with public token */}
      <ClaimsFormular publicToken={token} customerName={customerName} />
    </div>
  );
}
